import { forwardRef, useId } from 'react';

import { ChangeHandler } from 'react-hook-form';
import Label from './Label';
import Error, { type ErrorMessage } from './Error';
import clsx from 'clsx';

export interface RangeInputFieldProps {
  label?: string;
  description?: string;
  name?: string;
  id?: string;
  disabled?: boolean;
  additionalContainerClasses?: string;
  onChange?: ChangeHandler | ((e: React.ChangeEvent<HTMLInputElement>) => void);
  onBlur?: ChangeHandler;
  error?: ErrorMessage;
  value?: string | number;
  min?: string;
  max?: string;
  step?: string;
}

export const RangeInputField = forwardRef<HTMLInputElement, RangeInputFieldProps>(
  function InternalRangeInputField(
    {
      label,
      description,
      name,
      id,
      additionalContainerClasses,
      onChange,
      onBlur,
      error,

      value,
      disabled,
      min,
      max,
      step,
    },
    ref
  ) {
    const defaultId = useId();
    id = id ?? defaultId;

    return (
      <div
        className={clsx('relative gap-2 flex flex-col w-full', additionalContainerClasses)}
        id={id}
      >
        {label ? (
          <Label htmlFor={`${id}::range`} description={description}>
            {label}
          </Label>
        ) : null}
        <div className="flex relative w-full">
          <input
            onChange={onChange}
            onBlur={onBlur}
            name={name}
            id={`${id}::range`}
            disabled={disabled}
            defaultValue={value}
            type="range"
            min={min}
            max={max}
            step={step}
            className="w-full cursor-pointer appearance-none disabled:opacity-50 disabled:pointer-events-none focus:outline-none
          [&::-webkit-slider-thumb]:w-4
          [&::-webkit-slider-thumb]:h-4
          [&::-webkit-slider-thumb]:-mt-0.5
          [&::-webkit-slider-thumb]:appearance-none
          [&::-webkit-slider-thumb]:bg-blue-600
          
          [&::-webkit-slider-thumb]:rounded-full
          [&::-webkit-slider-thumb]:transition-all
          [&::-webkit-slider-thumb]:duration-150
          [&::-webkit-slider-thumb]:ease-in-out
          [&::-webkit-slider-thumb]:dark:bg-neutral-700
        
          [&::-moz-range-thumb]:w-4
          [&::-moz-range-thumb]:h-4
          [&::-moz-range-thumb]:appearance-none
          [&::-moz-range-thumb]:bg-blue-600
          [&::-moz-range-thumb]:border-4
          [&::-moz-range-thumb]:border-blue-600
          [&::-moz-range-thumb]:rounded-full
          [&::-moz-range-thumb]:transition-all
          [&::-moz-range-thumb]:duration-150
          [&::-moz-range-thumb]:ease-in-out
        
          [&::-webkit-slider-runnable-track]:w-full
          [&::-webkit-slider-runnable-track]:h-3
          [&::-webkit-slider-runnable-track]:bg-grey-400/50
          [&::-webkit-slider-runnable-track]:rounded-full
          [&::-webkit-slider-runnable-track]:dark:bg-neutral-700
        
          [&::-moz-range-track]:w-full
          [&::-moz-range-track]:h-2
          [&::-moz-range-track]:bg-gray-100
          [&::-moz-range-track]:rounded-full"
          />
        </div>

        <Error error={error} />
      </div>
    );
  }
);
